import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { themeVar } from '@/styles/theme';
import { ApolloError, ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { ROLE } from '@trace-8/trace8-schema';
import { useState } from 'react';
import styled from 'styled-components';
import { usersPageTabs } from '..';
import { LIST_ADVISORY_FIRM_USERS_QUERY, LIST_PLATFORM_MANAGER_USERS_QUERY, MAKE_PRIMARY_ADMIN_MUTATION } from '../queries';
import { IUser } from '../types';

interface MakePrimaryAdminProps {
  isOpen: boolean;
  activeTab: string;
  currentUser: IUser | null;
  handleFlashUpdatingRow: (id: string | null) => void;
  refetch: () => Promise<ApolloQueryResult<any>>;
  onClose: () => void;
}

const MakePrimaryAdmin = ({ isOpen, activeTab, currentUser, handleFlashUpdatingRow, refetch, onClose }: MakePrimaryAdminProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPrimaryAdminUserEmail, setCurrentPrimaryAdminUserEmail] = useState<string>('');

  const [makePrimaryAdmin, { loading: makePrimaryAdminLoading }] = useMutation(MAKE_PRIMARY_ADMIN_MUTATION);

  useQuery<{
    listPlatformManagerUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_PLATFORM_MANAGER_USERS_QUERY, {
    skip: activeTab !== usersPageTabs[0].value || !currentUser?.tenant?.id,
    variables: {
      filters: {
        limit: 1000,
        page: 1
      }
    },
    onCompleted: ({ listPlatformManagerUsers }) => {
      const defaultAdvisorEnumKey = getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN);
      const currentPrimaryAdminUser = listPlatformManagerUsers?.data?.find((user) => user.roles.includes(defaultAdvisorEnumKey));
      setCurrentPrimaryAdminUserEmail(currentPrimaryAdminUser?.email || '');
    }
  });

  useQuery<{
    listAdvisoryFirmUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_ADVISORY_FIRM_USERS_QUERY, {
    skip: activeTab !== usersPageTabs[1].value || !currentUser?.tenant?.id,
    variables: {
      filters: {
        tenantId: currentUser?.tenant?.id,
        limit: 1000,
        page: 1
      }
    },
    onCompleted: ({ listAdvisoryFirmUsers }) => {
      const defaultAdvisorEnumKey = getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN);
      const currentPrimaryAdminUser = listAdvisoryFirmUsers?.data?.find((user) => user.roles.includes(defaultAdvisorEnumKey));
      setCurrentPrimaryAdminUserEmail(currentPrimaryAdminUser?.email || '');
    }
  });

  const onSave = async () => {
    try {
      setErrorMessage(null);
      handleFlashUpdatingRow(currentUser.id);

      await makePrimaryAdmin({
        variables: {
          id: currentUser.id
        }
      });

      onClose();
      await refetch();
    } catch (err) {
      const error = err as ApolloError;
      console.error(error);
      setErrorMessage(error.message);
    } finally {
      handleFlashUpdatingRow(null);
    }
  };

  if (!currentUser) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Update Primary Admin"
      confirmButton={{
        name: 'Update Primary Admin',
        onClick: onSave,
        disabled: makePrimaryAdminLoading
      }}
      isUseOutsideClick={!makePrimaryAdminLoading}
      showCancelButton={!makePrimaryAdminLoading}
      showCloseIcon={!makePrimaryAdminLoading}
    >
      {makePrimaryAdminLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalDescription>
            This action will make <span style={{ fontWeight: 700 }}>{currentUser?.email}</span> the Primary Admin for{' '}
            <span style={{ fontWeight: 700 }}>{currentUser?.tenant?.name}</span>. The current Primary admin is{' '}
            <span style={{ fontWeight: 700 }}>{currentPrimaryAdminUserEmail}</span>. The Primary Admin is the most privileged user in the tenant and
            can configure permissions for other users.
          </ModalDescription>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default MakePrimaryAdmin;

const ModalDescription = styled.div`
  text-align: center;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
